import { getKernel, getKernelLoader } from '../../../Services/KernelRegistry'
import { RouteKernelLoader } from '../../../Commands/Loaders/RouteKernelLoader'

/**
 * Опції для конфігурації функції GetRouteUrlByKeyTask
 */
export interface GetRouteUrlByKeyOptions {
    /** Показувати попередження в консолі */
    showWarnings?: boolean
    /** Користувацька функція для обробки відсутніх маршрутів */
    onRouteNotFound?: (routeKey: string) => string | null
}

/**
 * Отримує URL маршруту за його ключем
 *
 * Ця функція автоматично сканує маршрути системи через ядро (Kernel) і повертає
 * відповідний шлях (path) для заданого ключа маршруту.
 *
 * @param routeKey - ключ маршруту (наприклад, 'Company.DriverProfile', 'Setting.Profile')
 * @param options - опції конфігурації для налаштування поведінки функції
 * @returns URL маршруту або null якщо маршрут не знайдено
 *
 * @example
 * Базове використання:
 * ```typescript
 * const url = GetRouteUrlByKeyTask('Company.DriverProfile')
 * // Повертає: '/drivers/profile'
 *
 * const url2 = GetRouteUrlByKeyTask('Setting.Profile')
 * // Повертає: '/users/profile'
 * ```
 *
 * @example
 * Використання з опціями:
 * ```typescript
 * const url = GetRouteUrlByKeyTask('Company.CustomRoute', {
 *   showWarnings: false,   // не показувати попередження
 *   onRouteNotFound: (key) => `/fallback/${key}`  // кастомна обробка
 * })
 * ```
 */
export const GetRouteUrlByKeyTask = (
    routeKey: string,
    options: GetRouteUrlByKeyOptions = {}
): string | null => {
    const { showWarnings = true, onRouteNotFound } = options

    // Валідація вхідних даних
    if (!routeKey || typeof routeKey !== 'string') {
        if (showWarnings) {
            console.warn('GetRouteUrlByKeyTask: routeKey має бути непорожнім рядком')
        }
        return null
    }

    // Отримання екземплярів ядра та завантажувача маршрутів
    let routeLoader: RouteKernelLoader

    try {
        getKernel()
        routeLoader = getKernelLoader('RouteKernelLoader') || getKernelLoader('route')

        if (!routeLoader) {
            if (showWarnings) {
                console.warn('GetRouteUrlByKeyTask: RouteKernelLoader не знайдено в ядрі')
            }
            return null
        }
    } catch (error) {
        if (showWarnings) {
            console.error('GetRouteUrlByKeyTask: Помилка отримання ядра або завантажувача маршрутів:', error)
        }
        return null
    }

    try {
        // Шукаємо маршрут за ключем
        const route = routeLoader.findRouteByKey(routeKey)

        if (route) {
            // Повертаємо шлях знайденого маршруту
            return route.path
        } else {
            // Маршрут не знайдено - обробляємо залежно від опцій
            if (onRouteNotFound) {
                // Використовуємо користувацьку функцію обробки
                return onRouteNotFound(routeKey)
            }

            if (showWarnings) {
                console.warn(`GetRouteUrlByKeyTask: маршрут з ключем "${routeKey}" не знайдено`)
            }

            return null
        }
    } catch (error) {
        // Обробка неочікуваних помилок
        if (showWarnings) {
            console.error(`GetRouteUrlByKeyTask: Помилка обробки ключа "${routeKey}":`, error)
        }
        return null
    }
}
